/*
 * TTTech nerve-management-system-frontend
 * Copyright(c) 2025 TTTech Industrial Automation AG.
 *
 * ALL RIGHTS RESERVED.
 * Usage of this software, including source code, netlists, documentation,
 * is subject to restrictions and conditions of the applicable license
 * agreement with TTTech Industrial Automation AG or its affiliates.
 *
 * All trademarks used are the property of their respective owners.
 * TTTech Industrial Automation AG and its affiliates do not assume any liability
 * arising out of the application or use of any product described or shown
 * herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 * make changes, at any time, in order to improve reliability, function or
 * design.
 *
 * Contact Information:
 * support@tttech-industrial.com
 * TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 */

import { defineStore } from 'pinia';
import byteSize from 'byte-size';
import { VolumesApiService } from '@/services/api';
import BackupInfoModel from '@/model/docker-resources/backupInfo.model';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import logger from '@/plugins/logger';

const useVolumesStore = defineStore('volumes', {
  state: () => ({
    volumes: [],
    isLoadingVolumes: false,
  }),

  actions: {
    async fetchVolumes(data) {
      this.isLoadingVolumes = true;
      try {
        const response = await VolumesApiService.getListOfVolumes(data);
        if (response?.volumes) {
          // Sort volumes by createdAt in descending order, and by name alphabetically if createdAt is the same
          const sortedVolumes = response.volumes.sort((a, b) => {
            const dateComparison = new Date(b.createdAt) - new Date(a.createdAt);
            if (dateComparison !== 0) {
              return dateComparison;
            }
            return a.name.localeCompare(b.name);
          });
          const listOfVolumes = sortedVolumes.map((volume) => {
            const sizeInfo = this.convertBytesToHumanReadable(volume.size);
            return {
              name: volume.name,
              size: {
                bytes: volume.size,
                humanReadable: `${sizeInfo.value} ${sizeInfo.unit}`,
              },
              usedByWorkloads: volume.usedByWorkloads.map((workload) => workload.versionName).join(', '),
              usedByContainers: volume.usedByContainers.map((container) => container).join(', '),
              ...(volume.backupInfo && { backupInfo: volume.backupInfo.map((backup) => new BackupInfoModel(backup)) }),
              created: volume.createdAt,
            };
          });
          this.volumes = listOfVolumes;
        }
      } catch (e) {
        const utilsStore = useUtilsStore();
        utilsStore.showCustomToast({
          text: e,
          color: 'red',
        });
        logger.error(`VolumesStore::fetchVolumes:: Error when fetching volumes: ${e.message}`);
      } finally {
        this.isLoadingVolumes = false;
      }
    },
    async deleteVolume({ name, serialNumber }) {
      const utilsStore = useUtilsStore();
      try {
        await VolumesApiService.deleteVolume({ name, serialNumber });
        this.volumes = this.volumes.filter((volume) => volume.name !== name);
        utilsStore.showCustomToast({
          text: 'nodes.volumes.successfullyDeleted',
          color: 'success',
        });
      } catch (e) {
        utilsStore.showCustomToast({
          text: e,
          color: 'red',
        });
        logger.error(`VolumesStore::deleteVolume:: Error when deleting volume: ${e.message}`);
      }
    },
    async importDataToVolume({ name, serialNumber, formData }) {
      try {
        this.updateVolume({
          volumeName: name,
          backupInfo: {
            action: 'import',
            status: 'BACKUP_DOWNLOAD_IN_PROGRESS',
          },
        });
        await VolumesApiService.importDataToVolume({ name, serialNumber, formData });
      } catch (err) {
        this.updateVolume({
          volumeName: name,
          backupInfo: {
            action: 'import',
            status: 'FAILED',
            errorMsg: err?.response?.data?.[0]?.message || 'Unknown error',
          },
        });
        logger.error(`VolumesStore::importDataToVolume:: Error when importing data to volume: ${err.message}`);
      }
    },
    async exportDataFromVolume({ name, serialNumber }) {
      try {
        this.updateVolume({
          volumeName: name,
          backupInfo: {
            action: 'export',
            status: 'BACKUP_CREATION_IN_PROGRESS',
          },
        });
        await VolumesApiService.exportVolume({ name, serialNumber });
      } catch (err) {
        this.updateVolume({
          volumeName: name,
          backupInfo: {
            action: 'export',
            status: 'FAILED',
            errorMsg: err?.response?.data?.[0]?.message || 'Unknown error',
          },
        });
        logger.error(`VolumesStore::exportDataFromVolume:: Error when exporting data from volume: ${err.message}`);
      }
    },
    setVolumeBackupStatus(params) {
      const utilsStore = useUtilsStore();
      if (params.backupInfo.status === 'COMPLETED') {
        utilsStore.showCustomToast({
          text: `nodes.volumes.${params.backupInfo.action}Completed`,
          color: 'success',
          showClose: true,
          permanent: true,
        });
      }
      if (params.backupInfo.status === 'FAILED' && params.backupInfo.errorMsg) {
        const translatedErrorMsg = new BackupInfoModel(params.backupInfo).errorMsg;
        utilsStore.showCustomToast({
          text: translatedErrorMsg,
          color: 'red',
          showClose: true,
          permanent: true,
        });
      }
      if (params.backupInfo.status === 'CANCELLED') {
        utilsStore.showCustomToast({
          text: `nodes.volumes.${params.backupInfo.action === 'import' ? 'importCancelled' : 'exportCancelled'}`,
          color: 'primary',
          showClose: true,
          permanent: true,
        });
      }
      this.updateVolume(params);
    },
    async cancelBackupAction({ name, action, serialNumber }) {
      this.updateVolume({
        volumeName: name,
        isCancellingInProgress: true,
      });
      if (action === 'import') {
        await VolumesApiService.cancelVolumeImport({ name, serialNumber });
        this.updateVolume({
          volumeName: name,
          backupInfo: {
            action: 'import',
            status: 'CANCELLED',
          },
          isCancellingInProgress: false,
        });
      } else {
        const utilsStore = useUtilsStore();
        await VolumesApiService.cancelVolumeExport({ name, serialNumber });
        utilsStore.showCustomToast({
          text: 'nodes.volumes.exportCancelled',
          color: 'primary',
          showClose: true,
          permanent: true,
        });
        this.updateVolume({
          volumeName: name,
          backupInfo: { action: 'export', status: 'CANCELLED' },
          isCancellingInProgress: false,
        });
      }
    },

    updateVolume({ volumeName, backupInfo, volumeSize, isCancellingInProgress }) {
      // Map through the volumes to find the one that matches volumeName
      const volume = this.volumes.find((vol) => vol.name === volumeName);
      // Check if the volume exists and already has backupInfo and update it
      if (!volume) {
        return;
      }
      volume.isCancellingInProgress = isCancellingInProgress;
      if (isCancellingInProgress) {
        return;
      }
      if (
        typeof backupInfo === 'object' &&
        backupInfo.action === 'import' &&
        backupInfo.status === 'COMPLETED' &&
        volumeSize
      ) {
        const sizeInfo = this.convertBytesToHumanReadable(volumeSize);
        volume.size = {
          bytes: volumeSize,
          humanReadable: `${sizeInfo.value} ${sizeInfo.unit}`,
        };
      }
      if (!volume.backupInfo) {
        volume.backupInfo = [new BackupInfoModel(backupInfo)]; // If no backupInfo exists, initialize the array with the new backupInfo
        return;
      }

      const backup = volume.backupInfo.find((b) => b.action === backupInfo.action);
      if (!backup) {
        // Backup with the same action doesn't exist, so push a new one
        volume.backupInfo.push(new BackupInfoModel(backupInfo));
        return;
      }
      // Backup with the same action exists, so update it
      Object.assign(backup, new BackupInfoModel(backupInfo));
    },

    /**
     * @description Convert size in bytes to human-readable size
     * @param {number} bytes - size in bytes
     * @returns {object} containing value and unit attributes representing memory
     */
    convertBytesToHumanReadable(bytes) {
      return byteSize(bytes, { precision: 1 });
    },
  },
});

export default useVolumesStore;
